import type {DiffEntry} from '@github-ui/diff-lines/types'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {QueryKey} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {produce} from 'immer'
import {getQueryClient} from '@github-ui/react-core/query-client'
import type {ComparisonInfo} from '../../stores/slices/comparison-info'

interface QueryKeyFields {
  basePath: string
  selectedRange: {
    baseOid: string
    headOid: string
  }
  hideWhitespace: boolean
}

export function diffEntriesKey({basePath, selectedRange, hideWhitespace}: QueryKeyFields): QueryKey {
  return [
    PageData.diffEntries,
    basePath,
    `baseOid:${selectedRange.baseOid}`,
    `headOid:${selectedRange.headOid}`,
    `hideWhitespace:${hideWhitespace}`,
  ]
}

export async function loadDiffEntriesForPaths(
  variables: ComparisonInfo & {
    paths: string[]
    contextRanges?: {[path: string]: Array<[number, number]>}
    signal?: AbortSignal
    basePath: string
  },
) {
  const queryClient = getQueryClient()
  const queryKey = diffEntriesKey(variables)
  const newDiffEntries = await fetchDiffEntries(variables)

  if (newDiffEntries && newDiffEntries.length > 0) {
    queryClient.setQueryData(
      queryKey,
      produce((oldDiffEntriesData: DiffEntry[]) => {
        if (!oldDiffEntriesData) return newDiffEntries

        // Merge old and new without dupes
        for (const newEntry of newDiffEntries) {
          if (oldDiffEntriesData.find(oldEntry => oldEntry.pathDigest === newEntry.pathDigest)) {
            continue
          }
          oldDiffEntriesData.push(newEntry)
        }
      }),
    )
  }
  return newDiffEntries
}

/**
 * Fetches diff entries for given paths
 */
async function fetchDiffEntries({
  basePath,
  fullDiff,
  selectedRange,
  paths,
  contextRanges,
  signal,
  hideWhitespace,
}: ComparisonInfo & {
  paths: string[]
  contextRanges?: {[path: string]: Array<[number, number]>}
  signal?: AbortSignal
  basePath: string
}): Promise<DiffEntry[] | undefined> {
  const params = new URLSearchParams()
  params.append('paths', paths.join(','))

  if (contextRanges) {
    // `ctx` parameter is in the same order as the `paths` parameter.
    // Context ranges are comma-separated, and ranges for paths are
    // colon-separated.
    //
    // For example:
    //     ?paths=a,b&ctx=:1-3,4-7
    //
    // Means that path `a` needs no injected context ranges, and path `b` needs
    // lines 1-3, and 4-7 inclusive.

    const ctx = paths
      .map(path => {
        return (contextRanges[path] ?? []).map(([a, b]) => `${a}-${b}`).join(',')
      })
      .join(':')
    params.append('ctx', ctx)
  }

  params.append('w', hideWhitespace ? '1' : '0')

  if (selectedRange.baseOid === fullDiff.baseOid) {
    params.append('range', selectedRange.headOid)
  } else {
    params.append('range', `${selectedRange.baseOid}..${selectedRange.headOid}`)
  }

  const fetchUrl = `${basePath}/page_data/${PageData.diffEntries}?${params.toString()}`

  try {
    const response = await reactFetchJSON(fetchUrl, {signal})
    if (signal?.aborted) return undefined
    const data = await parseJSONWithBetterErrors(response)
    throwErrorsIfBadResponse(response, data)
    return data ?? undefined
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      return
    } else {
      throw error
    }
  }
}
